import { createContext, use, useCallback, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { setUnauthorizedHandler } from '../api/client'
import { fetchMe, signIn as signInRequest, signOut as signOutRequest } from '../api/auth'
import type { Credentials, User } from '../api/auth'
import type { Permission } from '../api/permissions'

export const SESSION_QUERY_KEY = ['session', 'me'] as const

export type SessionStatus =
  /** The first `GET /api/me` has not answered yet. */
  | 'loading'
  /** Somebody is signed in. */
  | 'authenticated'
  /** Nobody is signed in — the ordinary state of a visitor. */
  | 'anonymous'
  /** The server could not be reached or broke. Distinct from being signed out. */
  | 'unreachable'

export interface Session {
  readonly status: SessionStatus
  readonly user: User | null
  /**
   * Whether the signed-in person holds a permission.
   *
   * Used to shape the screen — hiding a control they cannot use. It is not a
   * security boundary: the server checks the same permission and refuses
   * regardless, which is the check that actually protects anything. Treat a
   * hidden button as tidiness, never as a lock.
   */
  readonly can: (permission: Permission) => boolean
  readonly signIn: (credentials: Credentials) => Promise<User>
  readonly signOut: () => Promise<void>
  readonly isSigningIn: boolean
  readonly isSigningOut: boolean
  readonly refresh: () => void
}

const SessionContext = createContext<Session | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // Any 401, from any call, means the session is gone. Dropping the cached user
  // is what turns that into the sign-in screen, wherever the person happened to be.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.setQueryData<User | null>(SESSION_QUERY_KEY, null)
    })
  }, [queryClient])

  const me = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    // A failed session check is not worth retrying three times before the
    // sign-in screen appears.
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

  const signInMutation = useMutation({
    mutationFn: signInRequest,
    onSuccess: (user) => {
      // Sign-in answers with the same body as `/api/me`, so there is nothing to
      // go and fetch.
      queryClient.setQueryData<User | null>(SESSION_QUERY_KEY, user)
    },
  })

  const signOutMutation = useMutation({
    mutationFn: signOutRequest,
    onSettled: () => {
      // Everything cached was cached on behalf of the person leaving. Clearing
      // the whole cache — not just the user — is what stops the next person to
      // sign in on this browser from seeing the previous one's threads.
      queryClient.clear()
      queryClient.setQueryData<User | null>(SESSION_QUERY_KEY, null)
    },
  })

  const user = me.data ?? null

  const status: SessionStatus = me.isPending
    ? 'loading'
    : me.isError
      ? 'unreachable'
      : user === null
        ? 'anonymous'
        : 'authenticated'

  const granted = useMemo(() => new Set<string>(user?.permissions ?? []), [user])

  const can = useCallback((permission: Permission) => granted.has(permission), [granted])

  const signInMutate = signInMutation.mutateAsync
  const signOutMutate = signOutMutation.mutateAsync
  const refetchMe = me.refetch

  const value = useMemo<Session>(
    () => ({
      status,
      user,
      can,
      signIn: (credentials) => signInMutate(credentials),
      signOut: async () => {
        await signOutMutate()
      },
      isSigningIn: signInMutation.isPending,
      isSigningOut: signOutMutation.isPending,
      refresh: () => {
        void refetchMe()
      },
    }),
    [
      status,
      user,
      can,
      signInMutate,
      signOutMutate,
      signInMutation.isPending,
      signOutMutation.isPending,
      refetchMe,
    ],
  )

  return <SessionContext value={value}>{children}</SessionContext>
}

export function useSession(): Session {
  const session = use(SessionContext)
  if (session === null) {
    throw new Error('useSession must be used inside <SessionProvider>')
  }
  return session
}

/** Shorthand for the common case of asking about one permission. */
export function useCan(): (permission: Permission) => boolean {
  return useSession().can
}
